import { Slot } from '@radix-ui/react-slot'
import { cva, type VariantProps } from 'class-variance-authority'
import * as React from 'react'

import { cn } from '@/utils'

export const baseButtonCls = cva(
	'inline-flex cursor-pointer items-center justify-center whitespace-nowrap rounded-8 transition disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none',
	{
		variants: {
			visual: {
				default:
					'bg-blue text-white [--pulse-color-1:theme(colors.blue-50)] [--pulse-color-2:theme(colors.blue-0)] disabled:bg-grey disabled:opacity-100 hover:bg-blue-hover',
				secondary:
					'bg-white text-blue ring-1 ring-inset ring-blue hover:bg-blue hover:text-white',
				'secondary-inverted':
					'bg-transparent text-white ring-2 ring-inset ring-white hover:bg-white hover:text-black',
				soft: 'bg-white text-dark-grey ring-1 ring-inset ring-border-grey hover:text-blue hover:ring-blue',
				'soft-shadow':
					'bg-white font-normal text-dark-grey shadow-[0px_1px_4px_0px_#3F486729] ring-1 ring-inset ring-border-grey',
				ghost: 'bg-white text-dark-grey hover:bg-purple-8 hover:text-blue',
				'ghost-accent': 'bg-transparent px-2 text-blue hover:bg-purple-8',
				text: 'type-body-medium text-dark-grey hover:text-blue',
			},
			size: {
				default: 'type-button-small h-10 gap-2 px-4',
				large: 'type-button-large h-12 gap-2 px-6',
				icon: 'size-10 px-0',
				'icon-large': 'size-12 px-0',
			},
			pulsate: {
				true: 'animate-pulse-shadow',
			},
		},
		defaultVariants: {
			visual: 'default',
			size: 'default',
		},
		compoundVariants: [
			{
				visual: 'default',
				size: 'default',
				class: 'px-6',
			},
			{
				visual: 'secondary',
				size: 'default',
				class: 'px-6',
			},
			{
				visual: 'ghost-accent',
				size: 'default',
				class: 'px-2',
			},
			{
				visual: 'text',
				size: 'default',
				class: 'h-6 gap-1 p-0',
			},
		],
	},
)

export type ButtonVisualVariant = VariantProps<typeof baseButtonCls>['visual']

export type ButtonSizeVariant = VariantProps<typeof baseButtonCls>['size']

export interface ButtonProps
	extends React.ButtonHTMLAttributes<HTMLButtonElement>,
		VariantProps<typeof baseButtonCls> {
	asChild?: boolean
	pulsate?: boolean
}

export const BaseButton = React.forwardRef<HTMLButtonElement, ButtonProps>(
	(
		{ className, visual, size, pulsate = false, asChild = false, ...props },
		ref,
	) => {
		const Comp = asChild ? Slot : 'button'

		return (
			<Comp
				className={cn(
					baseButtonCls({ visual, size, pulsate, className }),
					className,
				)}
				ref={ref}
				{...props}
			/>
		)
	},
)
BaseButton.displayName = 'Button'
